// shared/utils/modulesConversion.ts
// Put this in your shared package and import from both client & admin.
// Filename suggestion: shared/utils/modulesConversion.ts

export type PageModuleRaw =
  | string
  | {
      module: string;      // instance name used as ComponentName
      sort_index?: number; // ordering index
      [k: string]: any;
    };

export type PageModuleObj = {
  module: string;
  sort_index: number;
  [k: string]: any;
};

/**
 * Returns true when EVERY element is an object with a 'module' prop.
 * This is a strict check to avoid mis-detecting mixed arrays.
 */
export const isNewModulesFormat = (arr?: PageModuleRaw[]): arr is PageModuleObj[] => {
  if (!Array.isArray(arr) || arr.length === 0) return false;
  return arr.every((item) => typeof item === 'object' && item !== null && Object.prototype.hasOwnProperty.call(item, 'module'));
};
export const insertModuleAtIndex = (modules: PageModuleRaw[] = [], index: number, moduleInstance: string): PageModuleObj[] => {
  const objs = ensureModulesObjects(modules);
  const idx = Math.max(0, Math.min(index, objs.length));
  const arr = [...objs.slice(0, idx), { module: moduleInstance }, ...objs.slice(idx)];
  return arr.map((m, i) => ({ ...m, sort_index: i + 1 }));
};


/**
 * Convert any modules array to array of PageModuleObj.
 * - If already object-format (with optional metadata) returns normalized objects with sequential sort_index.
 * - If it's string[] or mixed, converts strings -> objects and preserves objects, then sets sequential sort_index.
 * - Returns [] for falsy input.
 *
 * Idempotent and defensive.
 */
export const ensureModulesObjects = (modules?: PageModuleRaw[]): PageModuleObj[] => {
  if (!Array.isArray(modules) || modules.length === 0) return [];
  return modules.map((m, idx) => {
    if (typeof m === 'string') {
      return { module: m, sort_index: idx + 1 };
    }
    // m is object: keep other metadata, but ensure module exists
    const mod = m as PageModuleObj;
    return { ...mod, module: mod.module ?? (String(mod as any) ?? `unknown_${idx}`), sort_index: mod.sort_index ?? idx + 1 };
  }).map((m, idx) => ({ ...m, sort_index: idx + 1 })); // final pass: ensure sequential sort_index 1..n
};

/**
 * Get instance (module) names in the correct render order:
 * - For new-format objects: order by sort_index ascending.
 * - Otherwise assume string[] and return as is.
 */
export const getInstanceNamesFromPageModules = (pageModules?: PageModuleRaw[]): string[] => {
  if (!Array.isArray(pageModules) || pageModules.length === 0) return [];
  if (isNewModulesFormat(pageModules)) {
    return (pageModules as PageModuleObj[]).slice().sort((a, b) => (a.sort_index ?? Number.MAX_SAFE_INTEGER) - (b.sort_index ?? Number.MAX_SAFE_INTEGER)).map((m) => m.module);
  }
  // If mixed or strings, produce names by mapping: strings or object.module
  return pageModules.map((m) => (typeof m === 'string' ? m : (m as PageModuleObj).module));
};

/**
 * Reorder / move element: dragIndex -> hoverIndex.
 * - If the array contains any object (i.e. not pure strings), we treat it as object-format:
 *   convert to objects using ensureModulesObjects, move, then renormalize sort_index 1..n and return objects.
 * - If it's pure string[], we swap/move within strings and return string[].
 *
 * Returns new array (non-mutating).
 */
export const reorderModulesArray = (modules: PageModuleRaw[] = [], dragIndex: number, hoverIndex: number): PageModuleRaw[] => {
  if (!Array.isArray(modules) || modules.length === 0) return modules;
  const len = modules.length;
  if (dragIndex < 0 || dragIndex >= len || hoverIndex < 0 || hoverIndex >= len) return modules;

  const containsObjects = modules.some((m) => typeof m !== 'string');

  if (containsObjects) {
    const objs = ensureModulesObjects(modules);
    const arr = [...objs];
    const [item] = arr.splice(dragIndex, 1);
    arr.splice(hoverIndex, 0, item);
    return arr.map((m, idx) => ({ ...m, sort_index: idx + 1 }));
  }

  // pure string array
  const arr = [...(modules as string[])];
  const [item] = arr.splice(dragIndex, 1);
  arr.splice(hoverIndex, 0, item);
  return arr;
};
export const getModuleInstanceName = (item: PageModuleRaw): string => {
  if (!item) return '';
  if (typeof item === 'string') return item;
  // new-format objects: module is the instance name (defensive fallback to any known keys)
  return (item.module ?? (item as any).instance ?? '') as string;
};
/**
 * Remove module by instance name from modules array.
 * - Preserves input element shapes: strings stay strings, objects stay objects.
 * - For mixed arrays, it simply filters by matching instance name.
 */
export const removeModuleFromModulesArray = (modules?: PageModuleRaw[], instanceName?: string): PageModuleRaw[] => {
  if (!Array.isArray(modules) || !instanceName) return Array.isArray(modules) ? [...modules] : [];
  return modules.filter((m) => getModuleInstanceName(m) !== instanceName);
};

/**
 * Swap two indexes (keeps same semantics as previous swapIndexes).
 * - For object-format (or mixed), returns PageModuleObj[] with renormalized sort_index.
 * - For string arrays, returns string[] with swapped elements.
 *
 * Returns new array (non-mutating).
 */
export const swapIndexes = (arr: PageModuleRaw[] = [], index1: any, index2: any): PageModuleRaw[] => {
  if (!Array.isArray(arr) || index1 < 0 || index1 >= arr.length || index2 < 0 || index2 >= arr.length) {
    throw new Error('Invalid indexes or input array');
  }

  const containsObjects = arr.some((m) => typeof m !== 'string');

  if (containsObjects) {
    const objs = ensureModulesObjects(arr);
    const res = [...objs];
    [res[index1], res[index2]] = [res[index2], res[index1]];
    return res.map((m, idx) => ({ ...m, sort_index: idx + 1 }));
  }

  const res = [...(arr as string[])];
  [res[index1], res[index2]] = [res[index2], res[index1]];
  return res;
};

export default {
  isNewModulesFormat,
  ensureModulesObjects,
  getInstanceNamesFromPageModules,
  getModuleInstanceName,
  removeModuleFromModulesArray,
  reorderModulesArray,
  swapIndexes,
  insertModuleAtIndex
};
